import apiService from './apiService';
import { CSRF_COOKIE_NAME, getCookieValue, withCsrfHeader } from '../utils/csrfUtils';
import logger, { safeErrorEventDetails } from '../utils/logger';

export const authService = {
  async parseError(response, data, fallbackMessage) {
    const message = data?.message || data?.error || fallbackMessage;
    const error = new Error(message);
    error.status = response.status;
    if (data?.code) {
      error.code = data.code;
    }
    if (Number.isFinite(Number(data?.retryAfterMs))) {
      error.retryAfterMs = Math.max(0, Math.min(1000, Number(data.retryAfterMs)));
    }
    return error;
  },

  async signup(fullName, email, password) {
    try {
      const response = await apiService.request('/auth/signup', {
        method: 'POST',
        body: JSON.stringify({
          fullName: fullName.trim(),
          email: email.trim().toLowerCase(),
          password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw await this.parseError(response, data, 'Signup failed');
      }

      return data;
    } catch (error) {
      logger.errorEvent('signup_failed', safeErrorEventDetails(error));
      logger.error('Signup error:', error);
      throw error;
    }
  },

  async checkSignupEmail(email) {
    try {
      const response = await apiService.request('/auth/signup/check-email', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim().toLowerCase()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw await this.parseError(response, data, 'Unable to validate email');
      }

      return data;
    } catch (error) {
      logger.errorEvent('signup_email_check_failed', safeErrorEventDetails(error));
      logger.error('Signup email check error:', error);
      throw error;
    }
  },

  async login(email, password) {
    try {
      const response = await apiService.request('/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw await this.parseError(response, data, 'Login failed');
      }

      return data;
    } catch (error) {
      logger.errorEvent('login_failed', safeErrorEventDetails(error));
      logger.error('Login error:', error);
      throw error;
    }
  },

  async verifyEmailToken(token) {
    try {
      const response = await apiService.request(`/auth/verify-email-token/${token}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw await this.parseError(response, data, 'Verification failed');
      }

      return data;
    } catch (error) {
      logger.errorEvent('verify_email_failed', safeErrorEventDetails(error));
      logger.error('Email verification error:', error);
      throw error;
    }
  },

  async verify2FA(email, code, trustDevice) {
    try {
      const response = await apiService.request('/auth/verify-2fa', {
        method: 'POST',
        body: JSON.stringify({
          email,
          code,
          trustDevice
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw await this.parseError(response, data, 'Verification failed');
      }

      return data;
    } catch (error) {
      logger.errorEvent('verify_2fa_failed', safeErrorEventDetails(error));
      logger.error('2FA verification error:', error);
      throw error;
    }
  },

  async resend2FA(email) {
    try {
      const response = await apiService.request('/auth/resend-2fa', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim().toLowerCase()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw await this.parseError(response, data, 'Couldn’t resend the code');
      }

      return data;
    } catch (error) {
      logger.errorEvent('resend_2fa_failed', safeErrorEventDetails(error));
      logger.error('2FA resend error:', error);
      throw error;
    }
  },

  async getUserInfo(accessToken, { signal } = {}) {
    try {
      const response = await apiService.request('/auth/me', {
        signal,
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        let data = {};
        try {
          data = await response.json();
        } catch (_e) {
          data = {};
        }
        if (response.status === 403 && data.code === 'ACCESS_REVOKED') {
          throw new Error('ACCESS_REVOKED');
        }
        if (response.status === 403) {
          throw new Error('TOKEN_EXPIRED');
        }
        throw new Error(data.error || 'Failed to fetch user info');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      logger.errorEvent('get_user_info_failed', safeErrorEventDetails(error));
      logger.error('Get user info error:', error);
      throw error;
    }
  },

  async refreshToken(refreshToken) {
    try {
      const headers = withCsrfHeader({});
      const allowBody = process.env.NODE_ENV !== 'production';
      const body = allowBody && refreshToken ? JSON.stringify({ refreshToken }) : undefined;
      const response = await apiService.request('/auth/refresh', {
        method: 'POST',
        headers,
        body
      });

      let data = {};
      try {
        data = await response.json();
      } catch (_e) {
        data = {};
      }

      if (!response.ok) {
        const error = await this.parseError(response, data, 'Token refresh failed');
        if (data.code === 'ACCESS_REVOKED') {
          error.message = 'ACCESS_REVOKED';
        }
        throw error;
      }

      if (!data?.accessToken || typeof data.accessToken !== 'string') {
        throw new Error('Invalid token refresh response');
      }
      return data;
    } catch (error) {
      logger.errorEvent('refresh_token_failed', safeErrorEventDetails(error));
      logger.error('Token refresh error:', error);
      throw error;
    }
  },

  async exchangeNativeSessionHandoff(handoffToken) {
    try {
      const response = await apiService.request('/auth/native-session-handoff/exchange', {
        method: 'POST',
        body: JSON.stringify({ handoffToken })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw await this.parseError(response, data, 'Secure session handoff failed');
      }

      if (!data?.accessToken || typeof data.accessToken !== 'string') {
        throw new Error('Invalid secure session handoff response');
      }

      return data;
    } catch (error) {
      logger.errorEvent('native_session_handoff_exchange_failed', safeErrorEventDetails(error));
      logger.error('Native session handoff exchange error:', error);
      throw error;
    }
  },

  async logout(refreshToken) {
    try {
      const headers = withCsrfHeader({});
      const options = { method: 'POST', headers };
      const allowBody = process.env.NODE_ENV !== 'production';
      if (allowBody && refreshToken) {
        options.body = JSON.stringify({ refreshToken });
      }
      await apiService.request('/auth/logout', options);
    } catch (error) {
      logger.errorEvent('logout_failed', safeErrorEventDetails(error));
      logger.error('Logout error:', error);
      // Don't throw - logout should always succeed locally
    }
  },

  getCsrfToken() {
    return getCookieValue(CSRF_COOKIE_NAME);
  },

  async deleteAccount(password, accessToken) {
    try {
      const response = await apiService.authenticatedRequest('/auth/delete-account', {
        method: 'DELETE',
        body: JSON.stringify({ password })
      }, accessToken);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete account');
      }

      return data;
    } catch (error) {
      logger.errorEvent('delete_account_failed', safeErrorEventDetails(error));
      logger.error('Delete account error:', error);
      throw error;
    }
  },

  async requestPasswordReset(email) {
    try {
      const response = await apiService.request('/auth/request-password-reset', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }
      return data;
    } catch (error) {
      logger.errorEvent('password_reset_request_failed', safeErrorEventDetails(error));
      logger.error('Password reset request error:', error);
      throw error;
    }
  },

  async validateResetToken(token) {
    try {
      const response = await apiService.request(`/auth/reset-password/validate/${token}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Invalid or expired token');
      }
      return data;
    } catch (error) {
      logger.errorEvent('password_reset_token_invalid', safeErrorEventDetails(error));
      throw error;
    }
  },

  async resetPassword(token, newPassword, confirmPassword) {
    try {
      const response = await apiService.request('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword, confirmPassword })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Reset failed');
      }
      return data;
    } catch (error) {
      logger.errorEvent('password_reset_failed', safeErrorEventDetails(error));
      throw error;
    }
  },

  async resendSignupLink(email) {
    try {
      const response = await apiService.request('/auth/resend-signup-link', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend link');
      }
      return data;
    } catch (error) {
      logger.errorEvent('resend_signup_link_failed', safeErrorEventDetails(error));
      throw error;
    }
  },

  async acceptInvite(token, password, confirmPassword, fullName) {
    try {
      const response = await apiService.request('/auth/accept-invite', {
        method: 'POST',
        body: JSON.stringify({ token, password, confirmPassword, ...(fullName ? { fullName } : {}) })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Invite acceptance failed');
      }
      return data;
    } catch (error) {
      logger.errorEvent('invite_accept_failed', safeErrorEventDetails(error));
      throw error;
    }
  }
};
